// src/pages/portal/Orders.tsx
import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, Link } from "react-router-dom";
import { ChevronLeft, RefreshCcw } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { EmptyState } from "@/components/ui/EmptyState";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";

import { usePortal } from "@/lib/portal/PortalContext";
import { type OrderStatus, type Order, type OrderLine } from "@/lib/demoData";
import { summarizeOrder, formatDiscountLabel } from "@/lib/pricing";
import { formatPrice } from "@/lib/formatPrice";
import { authHeaders } from "@/lib/api";
import { cn } from "@/lib/cn";

type StatusFilter = "all" | OrderStatus;

const STATUS_VARIANT: Record<OrderStatus, "default" | "warning" | "danger" | "success" | "teal"> = {
  draft: "default",
  submitted: "warning",
  approved: "teal",
  shipped: "teal",
  delivered: "success",
  cancelled: "danger",
};

// API shapes from /api/portal/orders (scoped by kc sub server-side)
interface ApiOrderLine {
  product_id: number | null;
  ean: string | null;
  label: string | null;
  quantity: number;
  unit_price_tvac: number | null;
  line_total_tvac: number | null;
}
interface ApiOrder {
  order_id: string;
  created_at: string | null;
  status: string;
  total_tvac: number | null;
  lines: ApiOrderLine[];
  fulfillment?: {
    fulfillment_id: string;
    status: string;
    tracking_number: string | null;
    parcel_status: string | null;
    simulated: boolean;
  } | null;
}

// Display copy for the order-status filter buttons and badges. The
// machine value (OrderStatus) is unchanged; only the label is translated.
const STATUS_LABEL_KEY: Record<string, string> = {
  all: "portal.orders.statusAll",
  draft: "portal.orders.statusDraft",
  submitted: "portal.orders.statusSubmitted",
  approved: "portal.orders.statusApproved",
  shipped: "portal.orders.statusShipped",
  delivered: "portal.orders.statusDelivered",
  cancelled: "portal.orders.statusCancelled",
};

const STATUS_MAP: Record<string, OrderStatus> = {
  new: "submitted",
  confirmed: "approved",
  cancelled: "cancelled",
};

function mapApiOrder(o: ApiOrder): Order {
  return {
    id: o.order_id,
    customerId: "",
    placedAt: o.created_at ?? "",
    status: STATUS_MAP[o.status] ?? "submitted",
    lines: o.lines.map((l): OrderLine => ({
      sku: l.product_id != null ? String(l.product_id) : (l.ean ?? "?"),
      name: l.label ?? "",
      brand: "",
      image: l.product_id != null ? `/api/products/${l.product_id}/image` : "",
      qty: l.quantity,
      unitPriceEUR: l.unit_price_tvac ?? 0,
      netUnitPriceEUR: l.unit_price_tvac ?? 0,
      tierDiscount: 0,
    })),
  };
}

export function PortalOrders() {
  const { t } = useTranslation();
  const { customer } = usePortal();
  const { orderId } = useParams<{ orderId?: string }>();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [orders, setOrders] = useState<Order[]>([]);
  const [fulfillments, setFulfillments] = useState<
    Record<string, NonNullable<ApiOrder["fulfillment"]>>
  >({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!customer) return;
    let cancelled = false;
    setLoading(true);
    fetch("/api/portal/orders", { headers: authHeaders() })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const body = (await r.json()) as { rows: ApiOrder[] };
        if (!cancelled) {
          setOrders(body.rows.map(mapApiOrder));
          setFulfillments(Object.fromEntries(
            body.rows.filter((o) => o.fulfillment)
              .map((o) => [o.order_id, o.fulfillment!]),
          ));
        }
      })
      .catch((e) => {
        if (!cancelled) setError(String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [customer]);

  const allMine = useMemo(
    () => [...orders].sort((a, b) => b.placedAt.localeCompare(a.placedAt)),
    [orders],
  );

  if (!customer) return null;

  const visible = statusFilter === "all" ? allMine : allMine.filter((o) => o.status === statusFilter);
  const detail = orderId ? allMine.find((o) => o.id === orderId) : null;

  if (detail) {
    return <OrderDetail order={detail} fulfillment={fulfillments[detail.id] ?? null} />;
  }

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="text-xl font-510 tracking-tight text-fg-primary">{t("portal.orders.title")}</h2>
        <div className="flex flex-wrap gap-1">
          {(["all", "draft", "submitted", "approved", "shipped", "delivered", "cancelled"] as StatusFilter[]).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setStatusFilter(f)}
              aria-pressed={statusFilter === f}
              className={cn(
                "rounded px-2 py-1 text-xs font-510 uppercase tracking-wider transition-colors",
                statusFilter === f
                  ? "bg-benchmark-teal/20 text-benchmark-teal-bright"
                  : "bg-bg-elevated text-fg-secondary hover:bg-bg-hover hover:text-fg-primary",
              )}
            >
              {t(STATUS_LABEL_KEY[f] ?? f)}
            </button>
          ))}
        </div>
      </header>

      {error && (
        <div className="rounded-md border border-accent-rose/30 bg-accent-rose/10 p-3 text-sm text-accent-rose">
          {t("portal.orders.apiError")} ({error})
        </div>
      )}

      {loading ? (
        <p className="py-8 text-center text-sm text-fg-tertiary">
          <RefreshCcw className="mr-2 inline h-4 w-4 animate-spin" />
          {t("portal.orders.loadingOrders")}
        </p>
      ) : visible.length === 0 && !error ? (
        <EmptyState
          title={t("portal.orders.empty")}
          description={t("portal.orders.emptyHint")} 
        />
      ) : (
        <div className="space-y-3">
          {visible.map((o) => {
            const summary = summarizeOrder(
              o.lines.map((l) => ({ listPrice: l.netUnitPriceEUR, tier: customer.pricingTier, qty: l.qty })),
            );
            const itemCount = o.lines.reduce((s, l) => s + l.qty, 0);
            return (
              <Link
                key={o.id}
                to={`/portal/orders/${o.id}`}
                className="block rounded-lg border border-border bg-bg-surface transition-colors hover:border-benchmark-teal/40"
              >
                <div className="flex flex-wrap items-center gap-3 px-5 py-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <code className="rounded bg-bg-elevated px-1.5 py-0.5 font-mono text-[10px] text-fg-secondary">{o.id}</code>
                      <Badge variant={STATUS_VARIANT[o.status]} size="sm">{t(STATUS_LABEL_KEY[o.status] ?? o.status)}</Badge>
                      <span className="text-[10px] text-fg-quaternary">
                        {t("portal.orders.placed")} {new Date(o.placedAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="mt-1 text-sm font-510 text-fg-primary">
                      {o.lines.length} SKU{o.lines.length === 1 ? "" : "s"} · {itemCount} {itemCount === 1 ? t("portal.orders.unit") : t("portal.orders.units")}
                    </div>
                  </div>
                  <div className="font-mono text-sm font-510 text-fg-primary">
                    {formatPrice(summary.subtotal, "EUR")}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

function OrderDetail({ order, fulfillment }: {
  order: Order;
  fulfillment: NonNullable<ApiOrder["fulfillment"]> | null;
}) {
  const { t } = useTranslation();
  const { customer } = usePortal();
  if (!customer) return null;
  const summary = summarizeOrder(
    order.lines.map((l) => ({ listPrice: l.netUnitPriceEUR, tier: customer.pricingTier, qty: l.qty })),
  );
  return (
    <div className="space-y-5">
      {/* M2-06: delivery tracking (DEMO carrier — clearly simulated) */}
      {fulfillment && (
        <Card>
          <CardContent className="flex flex-wrap items-center gap-2 p-4 text-sm">
            <Badge variant={fulfillment.status === "delivered" ? "success" : "teal"} size="sm">
              {fulfillment.status}
            </Badge>
            <span className="text-fg-secondary">{t("portal.orders.delivery")}</span>
            {fulfillment.tracking_number && (
              <>
                <code className="rounded bg-bg-elevated px-1.5 py-0.5 font-mono text-xs text-fg-primary">
                  {fulfillment.tracking_number}
                </code>
                <span className="text-xs text-fg-tertiary">
                  {(fulfillment.parcel_status ?? "").replace(/_/g, " ")}
                </span>
              </>
            )}
            <Badge variant="warning" size="sm">{t("portal.orders.demoDelivery")}</Badge>
          </CardContent>
        </Card>
      )}
      <Link
        to="/portal/orders"
        className="inline-flex items-center gap-1 text-sm text-fg-tertiary hover:text-fg-primary"
      >
        <ChevronLeft className="h-3 w-3" strokeWidth={1.5} /> {t("portal.orders.backToAllOrders")}
      </Link>
      <Card>
        <CardHeader className="flex flex-row items-start justify-between">
          <div>
            <CardTitle>{order.id}</CardTitle>
            <p className="text-xs text-fg-tertiary">
              {t("portal.orders.placed")} {new Date(order.placedAt).toLocaleString()}
            </p>
          </div>
          <Badge variant={STATUS_VARIANT[order.status]} size="md">
            {t(STATUS_LABEL_KEY[order.status] ?? order.status)}
          </Badge>
        </CardHeader>
        <CardContent className="divide-y divide-border p-0">
          {order.lines.map((l) => (
            <div key={l.sku} className="flex flex-wrap items-center gap-3 px-5 py-3">
              <div className="h-12 w-16 flex-shrink-0 overflow-hidden rounded-md border border-border">
                <img src={l.image} alt="" className="h-full w-full object-cover" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-[10px]">
                  <code className="rounded bg-bg-elevated px-1.5 py-0.5 font-mono text-fg-secondary">{l.sku}</code>
                  {l.tierDiscount > 0 && (
                    <Badge variant="teal" size="sm">{formatDiscountLabel(l.tierDiscount)} {t("portal.orders.off")}</Badge>
                  )}
                </div>
                <div className="mt-1 truncate text-sm font-510 text-fg-primary">{l.name}</div>
              </div>
              <div className="text-right text-sm">
                <div className="text-fg-quaternary">
                  {l.qty} × {formatPrice(l.netUnitPriceEUR, "EUR")}
                </div>
                <div className="font-mono font-510 text-fg-primary">
                  {formatPrice(l.netUnitPriceEUR * l.qty, "EUR")}
                </div>
              </div>
            </div>
          ))}
          <div className="border-t border-border bg-bg-panel/40 px-5 py-3">
            <div className="flex justify-between font-510 text-fg-primary">
              <span>{t("portal.orders.total")}</span>
              <span className="font-mono">{formatPrice(summary.subtotal, "EUR")}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Timeline */}
      {order.statusHistory && (
        <Card>
          <CardHeader>
            <CardTitle>{t("portal.orders.statusTimeline")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 p-5">
            {order.statusHistory.map((h, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <Badge variant={STATUS_VARIANT[h.status]} size="sm">{t(STATUS_LABEL_KEY[h.status] ?? h.status)}</Badge>
                <span className="text-fg-tertiary">
                  {new Date(h.at).toLocaleString()} · {h.actor ?? t("portal.orders.systemActor")}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
